import { useEffect, useState } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, TextField, Tooltip, Typography,
} from '@mui/material';
import { Add, DragIndicator, DeleteOutline, Sync, Edit } from '@mui/icons-material';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DATE_PRESETS, DEFAULT_DATE_PRESET } from '../../utils/date-presets';
import { SORTABLE_FIELDS } from '../../utils/user-settings';
import {
  capturePreset, matchingDatePreset,
  type PresetField, type PresetMatchState, type TrackingPreset,
} from '../../utils/tracking-presets';
import SavePresetDialog from './SavePresetDialog';
import PresetEditDialog from './PresetEditDialog';

const sortLabel = (field: string) => (field === 'name'
  ? 'Name' : SORTABLE_FIELDS.find((f) => f.value === field)?.label ?? field);

function summarise(p: TrackingPreset, vLabel: (f: string) => string): string {
  const parts: string[] = [];
  if (p.date_preset) parts.push(DATE_PRESETS.find((d) => d.key === p.date_preset)?.title ?? p.date_preset);
  if (p.group_by !== undefined) parts.push(`Group: ${vLabel(p.group_by)}`);
  if (p.drilldowns?.length) parts.push(`Drill: ${p.drilldowns.map(vLabel).join(' › ')}`);
  if (p.filters?.length) parts.push(`${p.filters.length} filter${p.filters.length > 1 ? 's' : ''}`);
  if (p.tags?.length) parts.push(`${p.tags.length} tag${p.tags.length > 1 ? 's' : ''}`);
  if (p.sort) parts.push(`Sort: ${sortLabel(p.sort.field)} ${p.sort.order === 'asc' ? '↑' : '↓'}`);
  return parts.join(' · ') || 'Sets nothing';
}

const fieldsOf = (p: TrackingPreset): Set<PresetField> => {
  const f = new Set<PresetField>();
  if (p.date_preset) f.add('date');
  if (p.group_by !== undefined) f.add('group');
  if (p.drilldowns !== undefined) f.add('drilldown');
  if (p.filters !== undefined) f.add('filters');
  if (p.tags !== undefined) f.add('tags');
  if (p.sort) f.add('sort');
  return f;
};

function SortableRow({ preset, vLabel, onRename, onEdit, onUpdate, onDelete }: {
  preset: TrackingPreset;
  vLabel: (f: string) => string;
  onRename: (name: string) => void;
  onEdit: () => void;
  onUpdate?: () => void;
  onDelete: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: preset.id });
  return (
    <Box
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}
      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.5 }}
    >
      <IconButton size="small" sx={{ cursor: 'grab', p: 0.3, touchAction: 'none' }} {...attributes} {...listeners}>
        <DragIndicator sx={{ fontSize: 18, color: 'text.secondary' }} />
      </IconButton>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <TextField
          variant="standard" size="small" fullWidth value={preset.name}
          onChange={(e) => onRename(e.target.value)}
          InputProps={{ disableUnderline: true, sx: { fontSize: '0.9rem', fontWeight: 500 } }}
        />
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
          {summarise(preset, vLabel)}
        </Typography>
      </Box>
      <Tooltip title="Edit fields">
        <IconButton size="small" onClick={onEdit}><Edit sx={{ fontSize: 18 }} /></IconButton>
      </Tooltip>
      {onUpdate && (
        <Tooltip title="Update to current view">
          <IconButton size="small" onClick={onUpdate}><Sync sx={{ fontSize: 18 }} /></IconButton>
        </Tooltip>
      )}
      <Tooltip title="Delete">
        <IconButton size="small" onClick={onDelete}><DeleteOutline sx={{ fontSize: 18 }} /></IconButton>
      </Tooltip>
    </Box>
  );
}

interface Props {
  open: boolean;
  onClose: () => void;
  presets: TrackingPreset[];
  /** Omit for context-free use (Settings modal): hides Save-current-view and ⟳ update-to-view. */
  state?: PresetMatchState;
  vLabel: (field: string) => string;
  onApply: (next: TrackingPreset[]) => void;
}

export default function PresetManagerDialog({ open, onClose, presets, state, vLabel, onApply }: Props) {
  const [items, setItems] = useState<TrackingPreset[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<TrackingPreset | null>(null);

  // seed on open only (re-seeding mid-edit drops a reorder)
  useEffect(() => {
    if (open) setItems(presets);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const addPreset = (p: TrackingPreset) => setItems((prev) => [...prev, p]);

  // explicit only — never an auto-update from the view
  const updateToView = (p: TrackingPreset) => {
    if (!state) return;
    const s = state;
    setItems((prev) => prev.map((x) => (x.id === p.id
      ? capturePreset(p.id, p.name, fieldsOf(p), s, matchingDatePreset(s.dateRange) ?? p.date_preset ?? DEFAULT_DATE_PRESET)
      : x)));
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 0.5 }}>Presets</DialogTitle>
        <DialogContent dividers sx={{ maxHeight: 460 }}>
          {items.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              {state ? 'No presets yet. Save the current view to make one.'
                : 'No presets yet. Create one from the Tracking page.'}
            </Typography>
          )}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={(e: DragEndEvent) => {
              const { active, over } = e;
              if (!over || active.id === over.id) return;
              setItems((prev) => arrayMove(
                prev, prev.findIndex((p) => p.id === active.id), prev.findIndex((p) => p.id === over.id),
              ));
            }}
          >
            <SortableContext items={items.map((p) => p.id)} strategy={verticalListSortingStrategy}>
              {items.map((p) => (
                <SortableRow
                  key={p.id}
                  preset={p}
                  vLabel={vLabel}
                  onRename={(name) => setItems((prev) => prev.map((x) => (x.id === p.id ? { ...x, name } : x)))}
                  onEdit={() => setEditing(p)}
                  onUpdate={state ? () => updateToView(p) : undefined}
                  onDelete={() => setItems((prev) => prev.filter((x) => x.id !== p.id))}
                />
              ))}
            </SortableContext>
          </DndContext>
          {state && (
            <Button startIcon={<Add />} size="small" sx={{ mt: 1 }} onClick={() => setCreateOpen(true)}>
              Save current view
            </Button>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => { onApply(items.filter((p) => p.name.trim())); onClose(); }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      {state && (
        <SavePresetDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          state={state}
          vLabel={vLabel}
          onSave={addPreset}
        />
      )}
      <PresetEditDialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        preset={editing}
        vLabel={vLabel}
        onSave={(updated) => setItems((prev) => prev.map((x) => (x.id === updated.id ? updated : x)))}
      />
    </>
  );
}
